/**
 * Subscription display helpers (#592).
 *
 * Display only: nothing here is an access gate. Payment gating is the backend's
 * job (HTTP 402, issue #603). Every branch keys on `subscription_state.access_status`
 * from `GET /api/v2/user`, never on the legacy `tier*` fields. The only legacy
 * fields read are `tier_premium` and `role_admin`, for the "hidden" rule.
 *
 * See the backend contract in biodroneportal `docs/SUBSCRIPTION_STATE_API.md`.
 */
import { humanFileSize } from '@utils/files';

const DAY_MS = 24 * 60 * 60 * 1000;

// access_status values that map 1:1 onto a view
const PASS_THROUGH = ['active', 'past_due', 'pending', 'inactive'];

/**
 * Whole calendar days from `now` until `endMs`, both normalised to local midnight
 * (the calculation the old TrialBanner used). Math.round rather than floor so a
 * DST change in between doesn't drop a day.
 *
 * @param {number} endMs End instant in milliseconds
 * @param {number} now Current instant in milliseconds
 * @returns {number} 0 or more
 */
const getDaysLeft = (endMs, now) => {
	const today = new Date(now);
	today.setHours(0, 0, 0, 0);
	const endDay = new Date(endMs);
	endDay.setHours(0, 0, 0, 0);
	return Math.max(0, Math.round((endDay - today) / DAY_MS));
};

/**
 * Derives which subscription UI to show for a user.
 *
 * - `hidden`: premium, an active organization, or an admin. Wins over everything.
 * - `none`: no `subscription_state` (null, undefined, or with no status at all).
 * - `trial_active` / `trial_ended`: the local trial (`status: 'local_trial'`). It has
 *   ended when `access_status` is `inactive`, or when `current_period_end` has passed.
 *   The second clause is defensive, for backends that don't derive it yet.
 * - `never_started`: `access_status: inactive` with Stripe status `incomplete_expired`. The
 *   first payment was never completed, so the subscription never started (it didn't "end").
 * - `active` / `past_due` / `pending` / `inactive`: `access_status` passed straight through.
 * - `unknown`: any other or missing `access_status`.
 *
 * @param {object|null|undefined} user The v2 profile (`currentUser`)
 * @param {number} [now=Date.now()] Current instant in milliseconds
 * @returns {{ view: 'hidden'|'trial_active'|'trial_ended'|'active'|'past_due'|'pending'|'inactive'|'never_started'|'unknown'|'none', daysLeft: number|null }}
 *   `daysLeft` is set only for `trial_active` with a known end date.
 */
export const getSubscriptionView = (user, now = Date.now()) => {
	if (user?.tier_premium || user?.active_org_id || user?.role_admin) {
		return { view: 'hidden', daysLeft: null };
	}

	const state = user?.subscription_state;
	if (!state || (state.status == null && state.access_status == null)) {
		return { view: 'none', daysLeft: null };
	}

	if (state.status === 'local_trial') {
		const endMs =
			typeof state.current_period_end === 'number' ? state.current_period_end * 1000 : null;
		// Second clause is defensive until every backend derives an expired trial as inactive.
		const ended = state.access_status === 'inactive' || (endMs !== null && endMs <= now);
		if (ended) return { view: 'trial_ended', daysLeft: null };
		return { view: 'trial_active', daysLeft: endMs === null ? null : getDaysLeft(endMs, now) };
	}

	if (state.access_status === 'inactive' && state.status === 'incomplete_expired') {
		return { view: 'never_started', daysLeft: null };
	}

	return {
		view: PASS_THROUGH.includes(state.access_status) ? state.access_status : 'unknown',
		daysLeft: null,
	};
};

/**
 * True once a purchase has landed: a working Stripe subscription, not the local trial.
 * Used by the checkout success page to stop polling.
 *
 * @param {object|null|undefined} state `subscription_state`
 * @returns {boolean}
 */
export const isSubscriptionActivated = state =>
	state?.access_status === 'active' && state?.status !== 'local_trial';

/**
 * The scheduled cancellation of a subscription that has been canceled but keeps
 * running until it ends (`access_status` stays `active` until then).
 *
 * A cancellation is scheduled when `cancel_at_period_end` is true, or when `cancel_at`
 * is set. The second clause is defensive: Stripe can schedule a cancellation for a
 * specific date without setting `cancel_at_period_end`. The end is `cancel_at`,
 * falling back to `current_period_end`.
 *
 * Returns an object rather than the bare date so callers can tell "canceled, end
 * date unknown" apart from "renewing".
 *
 * @param {object|null|undefined} state `subscription_state`
 * @returns {{ endsAt: number|null }|null} `endsAt` in unix seconds (null when no date
 *   is known), or null when no cancellation is scheduled
 */
export const getCancellation = state => {
	if (state?.cancel_at_period_end !== true && typeof state?.cancel_at !== 'number') {
		return null;
	}
	if (typeof state.cancel_at === 'number') return { endsAt: state.cancel_at };
	return {
		endsAt: typeof state.current_period_end === 'number' ? state.current_period_end : null,
	};
};

/**
 * Formats a unix timestamp (seconds) as an en-US long date, e.g. "October 14, 2025".
 *
 * @param {number|null|undefined} seconds
 * @returns {string|null} null when `seconds` is not a number
 */
export const formatPeriodDate = seconds =>
	typeof seconds === 'number'
		? new Date(seconds * 1000).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		: null;

const countLine = (n, singular, plural) =>
	`${n.toLocaleString('en-US')} ${n === 1 ? singular : plural}`;

/**
 * One display line per plan limit that is set (not null/undefined; 0 counts as set),
 * in the order the wording spec lists them.
 *
 * @param {object|null|undefined} state `subscription_state`
 * @returns {string[]} e.g. ['10 projects per month', '100 GB storage']
 */
export const getPlanLimitLines = state => {
	if (!state) return [];
	const lines = [];
	if (state.monthly_project_limit != null) {
		lines.push(
			countLine(state.monthly_project_limit, 'project per month', 'projects per month')
		);
	}
	if (state.project_images_limit != null) {
		lines.push(
			countLine(state.project_images_limit, 'image per project', 'images per project')
		);
	}
	if (state.monthly_detect_limit != null) {
		lines.push(
			countLine(state.monthly_detect_limit, 'detection per month', 'detections per month')
		);
	}
	if (state.monthly_training_limit != null) {
		lines.push(
			countLine(
				state.monthly_training_limit,
				'training run per month',
				'training runs per month'
			)
		);
	}
	if (state.storage_limit != null) {
		lines.push(`${humanFileSize(state.storage_limit)} storage`);
	}
	return lines;
};
